import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';

import { BaseComponent } from '../../../abstracts/base.abstract';
import { featuresWithPermissions } from '../../../data/permissions';
import { ModalParentIdEnum, ModalType } from '../../../enums';
import { MonthStatusEnum } from '../../../enums/booking-month-status.enum';
import { EventTypeEnum } from '../../../enums/event-type.enum';
import { IBookedEventsByMonth, IMonthlyEventData } from '../../../interfaces/event.interface';
import { ISchoolYear } from '../../../interfaces/groups/school-year.interface';
import { ModalService } from '../../../services';
import { ResetPreventGoBack } from '../../../store/actions/back-button.action';
import { SetEventsCurrentMonth } from '../../../store/actions/events.action';
import { DisableHomeworkSchoolFullMonth } from '../../../store/actions/homework-school.action';
import { DisableMealsFullMonth } from '../../../store/actions/meals.action';
import { BackButtonState } from '../../../store/state/back-button.state';
import { EventsState } from '../../../store/state/events.state';
import { getEndDateOfMonth, getMonthTrad, getStartDateOfMonth } from '../../../utils/date.utils';
import { ConfirmChangeMonthModalComponent } from '../../modals/confirm-change-month-modal/confirm-change-month-modal.component';
import { DisableMonthModalComponent } from '../../modals/disable-month-modal/disable-month-modal.component';
import { GetBookedHomeworkSchool } from './../../../store/actions/homework-school.action';
import { GetBookedMealsPerMonth } from './../../../store/actions/meals.action';

@Component({
    selector: 'app-events-booking-year',
    templateUrl: './events-booking-year.component.html',
    styleUrls: ['./events-booking-year.component.scss'],
})
export class EventsBookingYearComponent extends BaseComponent implements OnInit {
    @Select(EventsState.getCurrentMonthEventsData) public currentMonthEventsData$: Observable<IMonthlyEventData>;
    @Select(EventsState.getCurrentMonthBookedEvents) public currentMonthBookedEvents$: Observable<IBookedEventsByMonth>;
    @Select(EventsState.getFullEventsData) public currentSchoolYearData$: Observable<IMonthlyEventData[]>;

    @Input() public schoolYear: ISchoolYear;

    public selectedMonth: IMonthlyEventData;
    public selectedMonthBookedEvents: IBookedEventsByMonth;
    public currentSchoolYearEventsData: IMonthlyEventData[] = [];

    public newMonth: number;
    public previousMonth: number;
    public selectedMonthName: string = '';
    public monthEventsStatusEnum = MonthStatusEnum;
    public eventsType: EventTypeEnum;
    public eventsTypesEnum = EventTypeEnum;

    public permissions = featuresWithPermissions;

    @ViewChild('calendarContainer') public calendarContainer: ElementRef;

    constructor(private store: Store, private modalService: ModalService) {
        super();
    }

    ngOnInit(): void {
        this.uns = combineLatest([this.currentMonthEventsData$, this.currentMonthBookedEvents$, this.currentSchoolYearData$]).subscribe(
            ([currentMonthEventsData, currentMonthBookedEvents, fullEventsData]: [
                IMonthlyEventData,
                IBookedEventsByMonth,
                IMonthlyEventData[]
            ]) => {
                if (currentMonthEventsData && fullEventsData?.length) {
                    this.currentSchoolYearEventsData = fullEventsData;
                    this.selectedMonthBookedEvents = currentMonthBookedEvents;
                    this.selectedMonth = currentMonthEventsData;
                    this.selectedMonthName = currentMonthEventsData.monthName;
                    this.eventsType = this.selectedMonth.meals
                        ? EventTypeEnum.MEAL
                        : this.selectedMonth.homeworkSchool
                        ? EventTypeEnum.HOMEWORK
                        : null;
                    if (!this.newMonth) {
                        this.newMonth = currentMonthEventsData.month;
                    }
                }
            }
        );
    }

    public selectMonth(month: number): void {
        this.scrollToCalendar();
        if (this.newMonth === month) {
            return;
        }
        this.previousMonth = this.newMonth;
        this.newMonth = month;
        if (this.store.selectSnapshot(BackButtonState.getPreventGoBack)) {
            this.modalService.openModalWithComponent({
                type: ModalType.STANDARD,
                body: {
                    title: 'SHOP.SHARED.EVENTS.PREVENT_CHANGE_MONTH.TITLE',
                    cancelButtonLabel: 'SHOP.SHARED.EVENTS.CANCEL_BTN',
                    validateButtonLabel: 'SHOP.SHARED.EVENTS.CONFIRM_BTN',
                    cancelButtonFn: () => this.cancelChangeMonth(),
                    validateButtonFn: () => this.confirmChangeMonth(),
                },
                component: {
                    componentRef: ConfirmChangeMonthModalComponent,
                    parentIdRef: ModalParentIdEnum.BODY_CONTENT,
                },
            });
        } else {
            this.confirmChangeMonth();
        }
    }

    public confirmDisableMonth(selectedMonth: IMonthlyEventData): void {
        if (!selectedMonth || selectedMonth.meals?.length === 0 || selectedMonth.homeworkSchool?.length === 0) {
            return;
        }
        this.modalService.openModalWithComponent({
            type: ModalType.STANDARD,
            body: {
                title: 'SHOP.SHARED.EVENTS.CONFIRM_MODIFICATIONS',
                cancelButtonLabel: 'SHOP.SHARED.EVENTS.CANCEL_BTN',
                validateButtonLabel: 'SHOP.SHARED.EVENTS.CONFIRM_BTN',
                validateButtonFn: () => this.postDisableMonth(),
            },
            component: {
                componentRef: DisableMonthModalComponent,
                parentIdRef: ModalParentIdEnum.BODY_CONTENT,
                config: {
                    inputs: {
                        selectedMonth: this.selectedMonth,
                        selectedMonthName: this.selectedMonthName,
                    },
                },
            },
        });
    }

    public postDisableMonth(): void {
        const date: Date = new Date(
            this.selectedMonth.month < 7 ? this.schoolYear.end_year : this.schoolYear.start_year,
            this.selectedMonth.month,
            1
        );

        this.store
            .dispatch([
                this.eventsType === EventTypeEnum.MEAL
                    ? new DisableMealsFullMonth(date)
                    : this.eventsType === EventTypeEnum.HOMEWORK
                    ? new DisableHomeworkSchoolFullMonth(date)
                    : null,
            ])
            .subscribe(() => {
                this.refreshData();
            });
    }

    public refreshData(): void {
        const schoolHasSwitchToNextYear: boolean = this.schoolYear.start_year === new Date().getFullYear();
        const from: string = getStartDateOfMonth(this.newMonth, schoolHasSwitchToNextYear);
        const to: string = getEndDateOfMonth(this.newMonth, schoolHasSwitchToNextYear);

        if (this.eventsType === EventTypeEnum.MEAL) {
            this.store.dispatch(new GetBookedMealsPerMonth({ from, to })).subscribe(() => {
                this.store.dispatch(new SetEventsCurrentMonth(this.newMonth));
            });
        } else if (this.eventsType === EventTypeEnum.HOMEWORK) {
            this.store.dispatch(new GetBookedHomeworkSchool({ from, to })).subscribe(() => {
                this.store.dispatch(new SetEventsCurrentMonth(this.newMonth));
            });
        }
    }

    public confirmChangeMonth(): void {
        this.store.dispatch([new ResetPreventGoBack()]);
        this.selectedMonthName = getMonthTrad(this.newMonth);

        const schoolHasSwitchToNextYear: boolean = this.schoolYear.start_year === new Date().getFullYear();
        const from: string = getStartDateOfMonth(this.newMonth, schoolHasSwitchToNextYear);
        const to: string = getEndDateOfMonth(this.newMonth, schoolHasSwitchToNextYear);
        if (this.eventsType === EventTypeEnum.MEAL) {
            this.store.dispatch(new GetBookedMealsPerMonth({ from, to })).subscribe(() => {
                this.store.dispatch(new SetEventsCurrentMonth(this.newMonth));
            });
        } else if (this.eventsType === EventTypeEnum.HOMEWORK) {
            this.store.dispatch(new GetBookedHomeworkSchool({ from, to })).subscribe(() => {
                this.store.dispatch(new SetEventsCurrentMonth(this.newMonth));
            });
        }
    }

    public cancelChangeMonth(): void {
        this.newMonth = this.previousMonth;
    }

    public scrollToCalendar(): void {
        const element: HTMLElement = this.calendarContainer.nativeElement;

        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }
}
